import { MetastoreTableColumn } from '@konnektu/taiga';
import CodeBlockWriter from 'code-block-writer';
import { ExprValue } from '../card-editor/component-tree/component-tree-node.interface';
import { writeColumns } from '../config-transpiler/column-writer';
import { writeExprProperty } from '../config-transpiler/expr-printer';
import {
  BUILTIN_CREATE_ACTION,
  SectionConfigMetadata,
  TopAction,
  TopBarMetadata
} from '../config-transpiler/models';
import {
  orderRootEntries,
  writeGeneratorHeader
} from '../config-transpiler/payload-construction';
import {
  createPayloadWriter,
  normalizeCardBasePath,
  WriteFn,
  writeHorizontalStack,
  writeIconButton,
  writeJoined,
  writeProperty,
  writeRawProperty,
  writeSource,
  writeStringProperty
} from '../config-transpiler/payload-writer';
import { mapColumnConfigurationsToMetastoreTableColumns } from '../shared/column-configurator/column-configuration.mapper';
import { ColumnConfiguration } from '../shared/column-configurator/column-configurator.component';

export interface SectionPayloadParams {
  label: string;
  entityName: string;
  columns: ColumnConfiguration[];
  linkedCardPath: string | null;
  allowCreate: boolean;
  allowDelete: boolean;
  topActions?: TopAction[];
  tableSize?: 's' | 'm' | 'l';
  showManualFilter?: boolean;
  showFilterPresets?: boolean;
  importLegacyFolders?: boolean;
  prelude?: string;
  rootProps?: Array<[string, ExprValue]>;
  rootOrder?: string[];
  config?: SectionConfigMetadata;
  topBar?: TopBarMetadata;
}

export interface SectionPayloadResult {
  payload: string;
  metastoreTableColumns: MetastoreTableColumn[];
  searchColumns: string[];
}

export function generateSectionPayload(
  params: SectionPayloadParams
): SectionPayloadResult {
  const {
    label,
    entityName,
    columns,
    linkedCardPath,
    allowCreate,
    allowDelete,
    topActions,
    tableSize = 'm',
    showManualFilter = false,
    showFilterPresets = false,
    importLegacyFolders = false,
    prelude,
    rootProps,
    rootOrder,
    config,
    topBar
  } = params;

  const explicitConfig =
    config?.kind === 'generated' && config.explicit ? config : null;
  const explicit = explicitConfig !== null;
  const searchHint = explicitConfig ? searchColumnsHint(explicitConfig) : null;
  const columnIds = new Set(columns.map((col) => col.id));
  const searchColumns = orderByHint(
    [
      ...columns.filter((col) => col.isSearchable).map((col) => col.id),
      ...(searchHint ?? []).filter((id) => !columnIds.has(id))
    ],
    searchHint
  );
  const imported = <T extends string | boolean>(key: string, fallback: T): T =>
    explicitConfig ? importedLiteral(explicitConfig, key, fallback) : fallback;
  const importedSearch = imported('showSearch', false);
  const searchToggled =
    searchColumns.length > 0
      ? !importedSearch
      : importedSearch &&
        (searchHint?.some((id) => columnIds.has(id)) ?? false);
  const importedManualFilter = imported('showManualFilter', false);
  const importedPresets = imported('showFolders', false);
  const filtersToggled =
    showManualFilter !== importedManualFilter ||
    showFilterPresets !== importedPresets;

  const metastoreTableColumns = mapColumnConfigurationsToMetastoreTableColumns(
    columns,
    explicit
  );

  if (!explicit && !metastoreTableColumns.some((col) => col.id === 'Id')) {
    metastoreTableColumns.unshift({ id: 'Id' });
  }

  const initialSortColumn = columns.find((col) => col.initialSortDirection);
  const cardBasePath = normalizeCardBasePath(linkedCardPath);
  const effectiveTopActions = resolveTopActions(
    topActions,
    linkedCardPath,
    allowCreate
  );

  const writer = createPayloadWriter();
  writeGeneratorHeader(writer);
  if (prelude) {
    writer.write(prelude).newLineIfLastNot();
  }

  const configEntries: Array<[string, WriteFn]> = [
    ['entityName', (w) => writeStringProperty(w, 'entityName', entityName)],
    ['columns', (w) => writeColumns(w, metastoreTableColumns, columns)]
  ];
  if (!explicit || searchToggled) {
    configEntries.push([
      'showSearch',
      (w) =>
        writeRawProperty(
          w,
          'showSearch',
          searchColumns.length > 0 ? 'true' : 'false'
        )
    ]);
  }
  if (!explicit || searchColumns.length > 0 || searchHint) {
    configEntries.push([
      'searchColumns',
      (w) => writeRawProperty(w, 'searchColumns', JSON.stringify(searchColumns))
    ]);
  }
  if (!explicit || tableSize !== imported('tableSize', 'm')) {
    configEntries.push([
      'tableSize',
      (w) => writeStringProperty(w, 'tableSize', tableSize)
    ]);
  }
  if (!explicit || filtersToggled) {
    configEntries.push([
      'showFilters',
      (w) =>
        writeRawProperty(
          w,
          'showFilters',
          showManualFilter || showFilterPresets ? 'true' : 'false'
        )
    ]);
  }
  if (!explicit) {
    configEntries.push([
      'showRecordCount',
      (w) => writeRawProperty(w, 'showRecordCount', 'false')
    ]);
  }
  if (!explicit || showManualFilter !== importedManualFilter) {
    configEntries.push([
      'showManualFilter',
      (w) =>
        writeRawProperty(
          w,
          'showManualFilter',
          showManualFilter ? 'true' : 'false'
        )
    ]);
  }
  if (showFilterPresets !== importedPresets) {
    configEntries.push([
      'showFolders',
      (w) =>
        writeRawProperty(w, 'showFolders', showFilterPresets ? 'true' : 'false')
    ]);
  }
  if (showFilterPresets) {
    if (!importedPresets) {
      configEntries.push([
        'allowEditFolders',
        (w) => writeRawProperty(w, 'allowEditFolders', 'true')
      ]);
    }
    if (importLegacyFolders !== imported('importLegacyFolders', false)) {
      configEntries.push([
        'importLegacyFolders',
        (w) =>
          writeRawProperty(
            w,
            'importLegacyFolders',
            importLegacyFolders ? 'true' : 'false'
          )
      ]);
    }
  }
  if (initialSortColumn) {
    configEntries.push([
      'initialSort',
      (w) =>
        writeRawProperty(
          w,
          'initialSort',
          JSON.stringify({
            [initialSortColumn.id]: initialSortColumn.initialSortDirection
          })
        )
    ]);
  }
  if (linkedCardPath) {
    configEntries.push([
      'rowClick',
      (w) =>
        writeProperty(w, 'rowClick', (rw) =>
          rw
            .write('(event) => tenantNavigate([')
            .quote(cardBasePath)
            .write(', event.row.Id || event.row.id])')
        )
    ]);
  }
  if (topBar?.kind === 'raw') {
    configEntries.push([
      topBar.key,
      (w) => writeRawProperty(w, topBar.key, topBar.source)
    ]);
  } else if (effectiveTopActions.length > 0) {
    configEntries.push([
      'topContent',
      (w) => {
        w.write('topContent: ');
        writeTopContent(w, effectiveTopActions, cardBasePath);
        w.write(',').newLine();
      }
    ]);
  }
  if (allowDelete) {
    configEntries.push([
      'rowActions',
      (w) => {
        w.write('rowActions: ');
        writeDeleteRowAction(w, entityName);
        w.write(',').newLine();
      }
    ]);
  }
  const orderedConfigEntries = explicitConfig
    ? orderRootEntries(
        [
          ...configEntries,
          ...passthroughEntries(explicitConfig, configEntries)
        ],
        explicitConfig.order
      )
    : configEntries;

  const writeConfig: WriteFn = (w) => {
    w.write('config: ');
    if (config?.kind === 'raw') {
      writeSource(w, config.source);
      return;
    }
    w.inlineBlock(() => {
      orderedConfigEntries.forEach(([, render]) => render(w));
    });
  };

  const labelFromProps = rootProps?.some(([key]) => key === 'label') ?? false;
  const entries: Array<[string, WriteFn]> = [
    ...(label || !labelFromProps
      ? [['label', (w) => w.write('label: ').quote(label)] as [string, WriteFn]]
      : []),
    ['config', writeConfig],
    ...(rootProps ?? [])
      .filter(([key]) => !(key === 'label' && label))
      .map(([key, value]): [string, WriteFn] => [
        key,
        (w) => writeExprProperty(w, key, value)
      ])
  ];

  writer.write('return ').inlineBlock(() => {
    writeJoined(writer, orderRootEntries(entries, rootOrder), (w, [, render]) =>
      render(w)
    );
  });

  return {
    payload: writer.toString(),
    metastoreTableColumns,
    searchColumns
  };
}

function importedLiteral<T extends string | boolean>(
  config: Extract<SectionConfigMetadata, { kind: 'generated' }>,
  key: string,
  fallback: T
): T {
  const value = config.props?.find(([entryKey]) => entryKey === key)?.[1];
  return value?.kind === 'literal' && typeof value.value === typeof fallback
    ? (value.value as T)
    : fallback;
}

function searchColumnsHint(
  config: Extract<SectionConfigMetadata, { kind: 'generated' }>
): string[] | null {
  const hint = config.props?.find(([key]) => key === 'searchColumns')?.[1];
  if (hint?.kind !== 'array') return null;
  const ids = hint.items.map((item) =>
    item.kind === 'literal' && typeof item.value === 'string'
      ? item.value
      : null
  );
  return ids.every((id): id is string => id !== null) ? ids : null;
}

function orderByHint(ids: string[], hint: string[] | null): string[] {
  if (!hint) return ids;
  const rank = (id: string) => {
    const index = hint.indexOf(id);
    return index === -1 ? hint.length : index;
  };
  return [...ids].sort((a, b) => rank(a) - rank(b));
}

function passthroughEntries(
  config: Extract<SectionConfigMetadata, { kind: 'generated' }>,
  generated: Array<[string, WriteFn]>
): Array<[string, WriteFn]> {
  const printed = new Set(generated.map(([key]) => key));
  return (config.props ?? [])
    .filter(([key]) => !printed.has(key))
    .map(([key, value]): [string, WriteFn] => [
      key,
      (w) => {
        writeExprProperty(w, key, value);
        w.write(',').newLine();
      }
    ]);
}

function resolveTopActions(
  topActions: TopAction[] | undefined,
  linkedCardPath: string | null,
  allowCreate: boolean
): TopAction[] {
  if (topActions && topActions.length > 0) return topActions;
  if (linkedCardPath && allowCreate) return [BUILTIN_CREATE_ACTION];
  return [];
}

export function writeTopContent(
  writer: CodeBlockWriter,
  actions: TopAction[],
  cardBasePath: string
): void {
  const renderable = actions.filter(isActionRenderable);
  const items: WriteFn[] = renderable.map(
    (action) => (w) => writeActionButton(w, action, cardBasePath)
  );
  writeHorizontalStack(writer, items);
}

function isActionRenderable(action: TopAction): boolean {
  if (action.actionType === 'builtin') {
    return action.id === BUILTIN_CREATE_ACTION.id;
  }
  return action.actionType === 'navigate' || action.actionType === 'code';
}

function writeActionButton(
  writer: CodeBlockWriter,
  action: TopAction,
  cardBasePath: string
): void {
  writeIconButton(writer, { label: action.label, icon: action.icon }, (w) =>
    writeActionOnClick(w, action, cardBasePath)
  );
}

function writeActionOnClick(
  writer: CodeBlockWriter,
  action: TopAction,
  cardBasePath: string
): void {
  if (
    action.actionType === 'builtin' &&
    action.id === BUILTIN_CREATE_ACTION.id
  ) {
    writer
      .write('() => tenantNavigate([')
      .quote(cardBasePath)
      .write(', ')
      .quote('new')
      .write('])');
    return;
  }

  if (action.actionType === 'navigate') {
    writer
      .write('() => tenantNavigate([`')
      .write(action.navigatePath ?? '')
      .write('`])');
    return;
  }

  if (action.actionType === 'code') {
    writer.write(action.code ?? '() => {}');
    return;
  }
}

function writeDeleteRowAction(
  writer: CodeBlockWriter,
  entityName: string
): void {
  writer.write('[').newLine();
  writer.indent(() => {
    writer.inlineBlock(() => {
      writeStringProperty(writer, 'id', 'delete');
      writeStringProperty(writer, 'text', '');
      writeStringProperty(writer, 'icon', '@tui.trash');
      writer.write('onClick: (row) => ').inlineBlock(() => {
        writer
          .write('return metastore.delete(')
          .quote(entityName)
          .write(', row.Id || row.id).pipe(')
          .newLine();
        writer.indent(() => {
          writer.write('rxjs.tap((resp) => ').inlineBlock(() => {
            writer.write('if (resp.status === 1) ').inlineBlock(() => {
              writer
                .write('showTextAlert(')
                .quote('dynamicEditor.objectRemoveError')
                .write(', ')
                .quote('negative')
                .write(')');
            });
            writer.write(' else ').inlineBlock(() => {
              writer
                .write('showTextAlert(')
                .quote('dynamicEditor.objectRemoveSuccess')
                .write(', ')
                .quote('positive')
                .write(')')
                .newLine();
              writer.write('components().sectionTable.refresh()');
            });
          });
          writer.write(')').newLine();
        });
        writer.write(');').newLine();
      });
      writer.newLine();
    });
    writer.newLine();
  });
  writer.write(']');
}
